import React from "react";
import { useSelector } from "react-redux";
import {
  Header,
  SpaceBetween,
  TableProps,
} from "@amzn/awsui-components-react/polaris";

import { RootState } from "../../app/rootReducer";
import { FullAsinWithAudits } from "../../../from-server/asin-central/model";
import { CopyAllAsinsButton } from "./CopyAllAsinsButton";
import { ExportCsvButton } from "./ExportCsvButton";
import { formatMatchCount } from "./formatMatchCount";
import { SubmitCatalogDefaultsButton } from "./SubmitCatalogDefaultsButton";
import { SubmitPassExceptionsButton } from "./SubmitPassExceptionsButton";

export interface ValidationDashboardTableHeaderProps {
  readonly visibleColumnIds: readonly string[];
  readonly columnDefinitions: readonly TableProps.ColumnDefinition<FullAsinWithAudits>[];
  readonly selectedAsins: readonly FullAsinWithAudits[];
  readonly isEditing?: boolean;
  readonly editingServiceName?: string;
}

export const ValidationDashboardTableHeader: React.FC<
  ValidationDashboardTableHeaderProps
> = ({
  visibleColumnIds,
  columnDefinitions,
  selectedAsins,
  isEditing = false,
  editingServiceName,
}) => {
  const count = useSelector((state: RootState) => state.porAsinList.count);
  const isLoading = useSelector(
    (state: RootState) => state.porAsinList.isLoading
  );
  const error = useSelector((state: RootState) => state.porAsinList.error);

  const isLoadingWithoutPrior = isLoading && count === 0;
  const showCounter = !isLoadingWithoutPrior && error === null;

  if (isEditing) {
    return (
      <Header
        variant="h2"
        description={
          editingServiceName
            ? `Editing ${editingServiceName} validation results. Use the Save / Cancel buttons above the Status Overview to commit or discard.`
            : undefined
        }
      >
        Validation Dashboard
      </Header>
    );
  }

  const counterText = showCounter
    ? selectedAsins.length > 0
      ? `(${selectedAsins.length} selected / ${formatMatchCount(count)})`
      : `(${formatMatchCount(count)})`
    : undefined;

  return (
    <Header
      variant="h2"
      counter={counterText}
      actions={
        <SpaceBetween direction="horizontal" size="xs">
          <CopyAllAsinsButton selectedAsins={selectedAsins} />
          <ExportCsvButton
            visibleColumnIds={visibleColumnIds}
            columnDefinitions={columnDefinitions}
          />
          <SubmitCatalogDefaultsButton selectedAsins={selectedAsins} />
          <SubmitPassExceptionsButton selectedAsins={selectedAsins} />
        </SpaceBetween>
      }
    >
      Validation Dashboard
    </Header>
  );
};

export default ValidationDashboardTableHeader;
